import { Tower } from './Tower';
import { Monster } from '../monsters/Monster';
import { AudioManager, SoundEffect } from '../../systems/AudioManager';

export class MissileTower extends Tower {
  private missileBays: Phaser.GameObjects.Graphics[] = [];
  private launchReadyLights: Phaser.GameObjects.Graphics[] = [];

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, 80, 1.2, 150);
  }

  protected override createVisuals(): void {
    // Call parent satellite structure
    super.createVisuals();
    
    // Override with missile-specific satellite design
    this.createMissileSatelliteSpecific();
  }

  private createMissileSatelliteSpecific(): void {
    // Remove default and rebuild with missile systems
    this.removeAll();
    this.createSatelliteStructure();
    this.createSolarPanels();
    this.createMissileBaySystem(); // Missile-specific
    this.createTargetingRadar(); // Missile-specific
    this.createThrusters();
    this.createEnergyCore();
    this.createOrbitalMechanics();
  }

  private createMissileBaySystem(): void {
    // Main missile bay (vertical launch system)
    const mainBay = this.scene.add.graphics();
    
    // Bay housing
    mainBay.fillStyle(0x2c3e50, 1);
    mainBay.lineStyle(2, 0x34495e, 1);
    mainBay.fillRect(-8, -25, 16, 20);
    mainBay.strokeRect(-8, -25, 16, 20);
    
    // Missile launch tubes (6 tubes in 2x3 grid)
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 3; col++) {
        const tubeX = -4 + (col * 4);
        const tubeY = -20 + (row * 8);
        
        // Tube opening
        mainBay.fillStyle(0x1a252f, 1);
        mainBay.fillCircle(tubeX, tubeY, 2);
        mainBay.lineStyle(1, 0x34495e, 1);
        mainBay.strokeCircle(tubeX, tubeY, 2);
        
        // Missile indicator light
        const isReady = Math.random() > 0.3; // Random ready state
        const lightColor = isReady ? 0x2ecc71 : 0xe74c3c;
        const light = this.scene.add.graphics();
        light.fillStyle(lightColor, 0.8);
        light.fillCircle(tubeX + 3, tubeY - 3, 0.8);
        // mainBay.add(light);
        this.launchReadyLights?.push(light);
      }
    }
    
    // Bay doors (open position)
    mainBay.lineStyle(1, 0x7f8c8d, 1);
    mainBay.lineBetween(-8, -5, -12, -2);
    mainBay.lineBetween(8, -5, 12, -2);
    
    this.add(mainBay);
    this.missileBays?.push(mainBay);
    
    // Side-mounted missile pods
    for (let i = 0; i < 4; i++) {
      const angle = (Math.PI * 2 * i) / 4;
      const distance = 22;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;
      
      const sidePod = this.scene.add.graphics();
      
      // Pod housing
      sidePod.fillStyle(0x34495e, 1);
      sidePod.lineStyle(1, 0x2c3e50, 1);
      sidePod.fillRect(-3, -8, 6, 16);
      sidePod.strokeRect(-3, -8, 6, 16);
      
      // Missile silhouettes inside
      sidePod.fillStyle(0xe67e22, 0.8);
      sidePod.fillRect(-1.5, -6, 3, 12);
      sidePod.fillTriangle(0, -6, -1.5, -8, 1.5, -8);
      
      // Launch guidance fins
      sidePod.lineStyle(0.5, 0xf39c12, 0.8);
      sidePod.lineBetween(-1.5, 2, -2.5, 0);
      sidePod.lineBetween(1.5, 2, 2.5, 0);
      
      sidePod.setPosition(x, y);
      sidePod.setRotation(angle);
      this.add(sidePod);
      this.missileBays?.push(sidePod);
    }
    
    // Missile bay status animation
    this.scene.tweens.add({
      targets: mainBay,
      alpha: 0.8,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private createTargetingRadar(): void {
    // Advanced targeting radar array
    const radarArray = this.scene.add.graphics();
    
    // Radar dish (larger than basic satellite)
    radarArray.fillStyle(0x95a5a6, 1);
    radarArray.lineStyle(1, 0xbdc3c7, 1);
    radarArray.fillEllipse(0, 15, 20, 8);
    radarArray.strokeEllipse(0, 15, 20, 8);
    
    // Radar grid pattern
    radarArray.lineStyle(0.5, 0x2ecc71, 0.8);
    for (let i = -8; i <= 8; i += 2) {
      radarArray.lineBetween(i, 11, i, 19);
    }
    for (let i = 11; i <= 19; i += 2) {
      radarArray.lineBetween(-9, i, 9, i);
    }
    
    // Targeting computer array
    radarArray.fillStyle(0x34495e, 1);
    radarArray.fillRect(-6, 20, 12, 6);
    radarArray.lineStyle(1, 0x2c3e50, 1);
    radarArray.strokeRect(-6, 20, 12, 6);
    
    // Status LEDs
    for (let i = 0; i < 4; i++) {
      const ledX = -4.5 + (i * 3);
      radarArray.fillStyle(i % 2 === 0 ? 0x2ecc71 : 0xe74c3c, 0.9);
      radarArray.fillCircle(ledX, 23, 0.8);
    }
    
    this.add(radarArray);
    
    // Radar sweep animation
    this.scene.tweens.add({
      targets: radarArray,
      rotation: Math.PI * 2,
      duration: 4000,
      repeat: -1,
      ease: 'Linear'
    });
    
    // Targeting lock pulse
    this.scene.time.addEvent({
      delay: 2000,
      callback: () => this.createTargetingLock(),
      loop: true
    });
  }

  private createTargetingLock(): void {
    // Target acquisition pulse
    const lockPulse = this.scene.add.graphics();
    lockPulse.lineStyle(3, 0xe74c3c, 0.8);
    lockPulse.strokeCircle(0, 0, 8);
    lockPulse.lineStyle(1, 0xff6b6b, 1);
    lockPulse.strokeCircle(0, 0, 8);
    this.add(lockPulse);
    
    this.scene.tweens.add({
      targets: lockPulse,
      scaleX: 6,
      scaleY: 6,
      alpha: 0,
      duration: 1500,
      ease: 'Power2.easeOut',
      onComplete: () => lockPulse.destroy()
    });
    
    // Update ready lights
    this.updateMissileReadyLights();
  }

  private updateMissileReadyLights(): void {
    this.launchReadyLights.forEach((light, index) => {
      const isReady = Math.random() > 0.4;
      light.clear();
      light.fillStyle(isReady ? 0x2ecc71 : 0xe74c3c, 0.8);
      
      const positions = [
        { x: -4, y: -20 }, { x: 0, y: -20 }, { x: 4, y: -20 },
        { x: -4, y: -12 }, { x: 0, y: -12 }, { x: 4, y: -12 }
      ];
      
      if (positions[index]) {
        light.fillCircle(positions[index].x + 3, positions[index].y - 3, 0.8);
      }
    });
  }

  protected createMissileLaunchEffect(): void {
    // Launch bay doors animation
    this.missileBays.forEach((bay, index) => {
      const flash = this.scene.add.graphics();
      flash.fillStyle(0xf39c12, 0.9);
      flash.fillCircle(bay.x, bay.y, 8);
      flash.lineStyle(2, 0xe67e22, 1);
      flash.strokeCircle(bay.x, bay.y, 8);
      
      this.add(flash);
      
      this.scene.tweens.add({
        targets: flash,
        scaleX: 2.5,
        scaleY: 2.5,
        alpha: 0,
        duration: 600,
        delay: index * 100,
        ease: 'Power2.easeOut',
        onComplete: () => flash.destroy()
      });
    });
    
    // Launch exhaust trail
    const exhaust = this.scene.add.graphics();
    exhaust.fillStyle(0xff6b6b, 0.6);
    exhaust.fillTriangle(0, -5, -4, 5, 4, 5);
    exhaust.fillStyle(0xf39c12, 0.8);
    exhaust.fillTriangle(0, -5, -2, 2, 2, 2);
    
    this.add(exhaust);
    
    this.scene.tweens.add({
      targets: exhaust,
      scaleY: 3,
      alpha: 0,
      duration: 1000,
      ease: 'Power2.easeOut',
      onComplete: () => exhaust.destroy()
    });
  }

  protected override fireProjectile(target: Monster, damage: number, isCrit: boolean): void {
    // Create launch effect
    this.createMissileLaunchEffect();
    
    // Create enhanced missile
    const missile = this.createEnhancedMissile(isCrit);
    
    // Calculate travel time and trajectory
    const distance = Phaser.Math.Distance.Between(this.x, this.y, target.x, target.y);
    const travelTime = (distance / 200) * 1000; // Slower than regular projectiles
    
    // Create missile trail
    this.createMissileTrail(missile, travelTime);
    
    // Add some arc to the trajectory
    const midX = (this.x + target.x) / 2;
    const midY = Math.min(this.y, target.y) - 50; // Arc upward
    
    // Animate missile with curved path
    this.animateMissileWithArc(missile, target, midX, midY, travelTime, damage, isCrit);
  }

  private createEnhancedMissile(isCrit: boolean): Phaser.GameObjects.Container {
    const missileContainer = this.scene.add.container(this.x, this.y - 15);
    
    // Missile body
    const body = this.scene.add.ellipse(0, 0, 8, 3, isCrit ? 0xff4444 : 0x888888);
    missileContainer.add(body);
    
    // Missile tip
    const tip = this.scene.add.triangle(0, 0, -4, 0, 0, -2, 0, 2, isCrit ? 0xff0000 : 0xaaaaaa);
    missileContainer.add(tip);
    
    // Exhaust flame
    const flame = this.scene.add.ellipse(-4, 0, 6, 2, 0xff6600, 0.8);
    missileContainer.add(flame);
    
    // Flickering flame effect
    this.scene.tweens.add({
      targets: flame,
      scaleX: 1.5,
      scaleY: 1.5,
      alpha: 0.4,
      duration: 100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    if (isCrit) {
      // Crit glow
      const glow = this.scene.add.circle(0, 0, 8, 0xff0000, 0.3);
      missileContainer.add(glow);
    }
    
    return missileContainer;
  }

  private createMissileTrail(missile: Phaser.GameObjects.Container, duration: number): void {
    const trailTimer = this.scene.time.addEvent({
      delay: 30,
      repeat: Math.floor(duration / 30),
      callback: () => {
        // Exhaust trail
        const exhaust = this.scene.add.circle(missile.x - 8, missile.y, 2, 0xff6600, 0.7);
        
        this.scene.tweens.add({
          targets: exhaust,
          alpha: 0,
          scaleX: 2,
          scaleY: 2,
          duration: 500,
          ease: 'Power2',
          onComplete: () => exhaust.destroy()
        });
        
        // Smoke trail
        if (Math.random() < 0.5) {
          const smoke = this.scene.add.circle(
            missile.x - 6 + (Math.random() - 0.5) * 4, 
            missile.y + (Math.random() - 0.5) * 4, 
            1 + Math.random() * 2, 
            0x666666, 
            0.4
          );
          
          this.scene.tweens.add({
            targets: smoke,
            alpha: 0,
            scaleX: 3,
            scaleY: 3,
            duration: 800,
            ease: 'Power2',
            onComplete: () => smoke.destroy()
          });
        }
      }
    });
    
    this.scene.time.delayedCall(duration + 200, () => {
      trailTimer.destroy();
    });
  }

  private animateMissileWithArc(missile: Phaser.GameObjects.Container, target: Monster, midX: number, midY: number, travelTime: number, damage: number, isCrit: boolean): void {
    // Animate rotation to face direction
    const angle = Phaser.Math.Angle.Between(this.x, this.y, target.x, target.y);
    missile.setRotation(angle);
    
    // Create path tween
    this.scene.tweens.add({
      targets: missile,
      x: target.x,
      y: target.y,
      duration: travelTime,
      ease: 'Power2',
      onUpdate: (tween) => {
        // Update rotation to face movement direction
        if (tween.progress > 0.1) {
          const prevX = missile.x;
          const prevY = missile.y;
          
          this.scene.time.delayedCall(50, () => {
            const newAngle = Phaser.Math.Angle.Between(prevX, prevY, missile.x, missile.y);
            missile.setRotation(newAngle);
          });
        }
      },
      onComplete: () => {
        this.createMissileExplosion(target.x, target.y, isCrit, damage);
        
        // Apply damage with area effect
        this.applyExplosiveDamage(target, damage, isCrit);
        
        missile.destroy();
      }
    });
  }

  private createMissileExplosion(x: number, y: number, isCrit: boolean, damage: number): void {
    // Main explosion flash
    const explosion = this.scene.add.circle(x, y, isCrit ? 30 : 20, 0xffffff, 0.9);
    this.scene.tweens.add({
      targets: explosion,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      duration: 400,
      ease: 'Power2',
      onComplete: () => explosion.destroy()
    });
    
    // Fire explosion
    const fire = this.scene.add.circle(x, y, isCrit ? 25 : 18, 0xff4400, 0.8);
    this.scene.tweens.add({
      targets: fire,
      scaleX: 1.8,
      scaleY: 1.8,
      alpha: 0,
      duration: 600,
      ease: 'Power2',
      onComplete: () => fire.destroy()
    });
    
    // Explosion particles
    for (let i = 0; i < (isCrit ? 15 : 10); i++) {
      const angle = (Math.PI * 2 * i) / (isCrit ? 15 : 10);
      const distance = isCrit ? 35 : 25;
      
      const particle = this.scene.add.circle(x, y, 3, 0xff6600);
      
      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        duration: 600,
        delay: Math.random() * 100,
        ease: 'Power2',
        onComplete: () => particle.destroy()
      });
    }
    
    // Screen shake
    this.scene.cameras.main.shake(isCrit ? 200 : 150, isCrit ? 0.02 : 0.015);
    
    // Show damage number
    const damageText = this.scene.add.text(x, y - 30, `${damage.toFixed(0)}`, {
      fontSize: '16px',
      color: isCrit ? '#ff0000' : '#ffaa00',
      fontFamily: 'Arial',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: damageText,
      y: y - 60,
      alpha: 0,
      duration: 1000,
      ease: 'Power2',
      onComplete: () => damageText.destroy()
    });
  }

  private applyExplosiveDamage(target: Monster, damage: number, isCrit: boolean): void {
    // Apply damage to main target
    target.takeDamage(damage);
    
    // Area damage to nearby monsters
    const sceneData = (this.scene as any);
    const monsters: Monster[] = sceneData.monsters || [];
    const explosionRadius = isCrit ? 50 : 35;
    
    monsters.forEach((monster) => {
      if (monster !== target && monster.getIsActive() && monster.getHealth() > 0) {
        const distance = Phaser.Math.Distance.Between(target.x, target.y, monster.x, monster.y);
        if (distance <= explosionRadius) {
          const areaDamage = damage * 0.5; // 50% damage to nearby enemies
          monster.takeDamage(areaDamage);
        }
      }
    });
  }
} 